import { useState } from "react";
import { NavLink } from "react-router-dom";
import { Menu, X, FolderOpen, Settings, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import { useAuth } from "@/contexts/AuthContext";

type NavItem = {
  name: string;
  href: string;
  icon: React.ComponentType<any>;
  roles?: Array<"admin" | "user">; // optional gate
};

const Sidebar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { me, loading } = useAuth();
  const role = me?.role;

  const navigationItems: NavItem[] = [
    { name: "Projects", href: "/dashboard/projects", icon: FolderOpen, roles: ["admin", "user"] },
    { name: "Users", href: "/dashboard/users", icon: FolderOpen, roles: ["admin"] }, // gate
    { name: "Reports", href: "/dashboard/reports", icon: FileText, roles: ["admin", "user"] },
    { name: "Settings", href: "/dashboard/settings", icon: Settings, roles: ["admin"] },
  ];

  const visibleItems = navigationItems.filter((item) =>
    item.roles ? (!loading && role ? item.roles.includes(role) : false) : true
  );

  return (
    <>
      {/* Mobile menu button */}
      <Button
        variant="ghost"
        size="icon"
        className="lg:hidden fixed top-4 left-4 z-50"
        onClick={() => setIsOpen(!isOpen)}
      >
        {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
      </Button>

      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
          fixed top-0 left-0 h-full w-64 bg-muted z-50 transform transition-transform duration-300 ease-in-out
          lg:relative lg:translate-x-0 lg:z-auto
          ${isOpen ? "translate-x-0" : "-translate-x-full"}
        `}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center px-6">
            <Logo />
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4">
            <ul className="space-y-2">
              {visibleItems.map(item => (
                <li key={item.name}>
                  <NavLink
                    to={item.href}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                        isActive
                          ? "bg-primary text-primary-foreground shadow-md"
                          : "text-muted-foreground hover:text-foreground hover:bg-accent"
                      }`
                    }
                    onClick={() => setIsOpen(false)}
                  >
                    <item.icon className="h-5 w-5" />
                    {item.name}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>
    </>
  );
};

export default Sidebar;